import { GenerationTask } from 'Tasks/GenerationTask/GenerationTask'
import { IContainerStrategy } from '../ContainerStrategy'
import {
    ContainerDataType,
    ContainerType,
    RoleType,
    SourceType,
    TaskMode,
    ConversationResponseDataType,
    AttributesData,
    QCAttributesData,
    TaskConfigType,
    ValueDataType,
    AttributeDefinitionType,
} from 'Types/Tasks.types'
import { cloneDeep } from 'lodash'
import { isViewerMode } from 'Utils/task.utils'

export class GenerationContainerStrategy
    implements IContainerStrategy<ContainerType.ConversationResponse, ConversationResponseDataType>
{
    containerContext?: {
        task: GenerationTask
    }

    setContext(containerContext: { task: GenerationTask }) {
        this.containerContext = containerContext
    }

    getEmptyDraftContainer(): ContainerDataType<
        ContainerType.ConversationResponse,
        ConversationResponseDataType
    > {
        return {
            data: {
                schema_version: 1,
                type: ContainerType.ConversationResponse,
                data: {
                    response: {
                        id: crypto.randomUUID(),
                        role: RoleType.ASSISTANT,
                        source: SourceType.HUMAN,
                        content: {
                            data: {
                                value: '',
                                value_type: 'HTML',
                            },
                        },
                        extra_info: {},
                        version_id: null,
                    },
                    quality_check: {
                        evaluation_on_response_version_id: {},
                    },
                },
            },
        }
    }

    getContainerFromOutputStart(
        outputStartContainer: ContainerDataType<
            ContainerType.ConversationResponse,
            ConversationResponseDataType
        >,
        mode: TaskMode
    ): ContainerDataType<ContainerType.ConversationResponse, ConversationResponseDataType> {
        const draftContainer = cloneDeep(outputStartContainer)

        if (draftContainer && !isViewerMode(mode)) {
            draftContainer.data.data.quality_check.evaluation_on_response_version_id = {}
            draftContainer.decision = null
        }

        return draftContainer
    }

    getModifiedDraftOutputContainer = (
        draftOutputContainer: ContainerDataType<
            ContainerType.ConversationResponse,
            ConversationResponseDataType
        >,
        taskConfig: TaskConfigType<ContainerType.ConversationResponse, ConversationResponseDataType>
    ) => {
        const draftContainer = cloneDeep(draftOutputContainer)

        const extraInfo: AttributesData = {}
        const qcAttributes: { [versionId: string]: QCAttributesData } = {}

        if (!draftContainer) {
            return draftContainer
        }

        const versionIds = Object.keys(
            draftContainer.data.data.quality_check.evaluation_on_response_version_id ?? {}
        )

        taskConfig.assignment.task.attributes.forEach(attribute => {
            if (
                draftContainer.data.data.response.extra_info?.[attribute.name] &&
                attribute.type === AttributeDefinitionType.EXTRA_INFO
            ) {
                extraInfo[attribute.name] = draftContainer.data.data.response.extra_info?.[
                    attribute.name
                ] as ValueDataType<never>
            }

            versionIds.forEach(versionId => {
                if (
                    draftContainer.data.data.quality_check.evaluation_on_response_version_id?.[
                        versionId
                    ]?.[attribute.name] &&
                    attribute.type === AttributeDefinitionType.QUALITY_EVALUATION
                ) {
                    qcAttributes[versionId] = {
                        ...qcAttributes[versionId],
                        [attribute.name]:
                            draftContainer.data.data.quality_check
                                .evaluation_on_response_version_id[versionId][attribute.name],
                    } as QCAttributesData
                }
            })
        })
        draftContainer.data.data.response.extra_info = extraInfo
        draftContainer.data.data.quality_check.evaluation_on_response_version_id = qcAttributes

        return draftContainer
    }
}
